import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./EditCommunity.scss";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import DynamicHeader from "../Header/DynamicHeader";
import Cropper from "react-easy-crop";
import Alerts from "../Alerts/Alerts";

const DEFAULT_COMMUNITY_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/communities/default.png";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

async function getCroppedBlob(imageSrc, cropPixels, outSize = 512) {
  const image = await createImage(imageSrc);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context error");

  canvas.width = outSize;
  canvas.height = outSize;

  const sx = clamp(Math.round(cropPixels.x), 0, image.width);
  const sy = clamp(Math.round(cropPixels.y), 0, image.height);
  const sw = clamp(Math.round(cropPixels.width), 1, image.width - sx);
  const sh = clamp(Math.round(cropPixels.height), 1, image.height - sy);

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outSize, outSize);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

const EditCommunity = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [showLocation, setShowLocation] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [previousAvatarUrl, setPreviousAvatarUrl] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isAvatarMenuClosing, setIsAvatarMenuClosing] = useState(false);

  const avatarMenuRef = useRef(null);

  const [isAvatarCropOpen, setIsAvatarCropOpen] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState(null);
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const avatarCropSrcRef = useRef(null);

  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const pendingLeaveRef = useRef(null);
  const pendingHrefRef = useRef(null);
  const pendingHistoryBackRef = useRef(false);

  const allowPopRef = useRef(false);
  const lastUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash
  );
  const navHistory = window.history;

  const getAuthHeaders = useCallback(async (extra = {}) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error("No token");
    }

    return {
      ...extra,
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const stableStringify = useCallback((obj) => {
    const seen = new WeakSet();
    const sorter = (value) => {
      if (value === null || typeof value !== "object") return value;
      if (seen.has(value)) return value;
      seen.add(value);

      if (Array.isArray(value)) return value.map(sorter);

      const keys = Object.keys(value).sort();
      const out = {};
      keys.forEach((k) => (out[k] = sorter(value[k])));
      return out;
    };

    try {
      return JSON.stringify(sorter(obj));
    } catch (e) {
      return "";
    }
  }, []);

  const buildSnapshot = useCallback(
    (override = {}) => {
      const snap = {
        name: String(override.name ?? name ?? ""),
        description: String(override.description ?? description ?? ""),
        location: String(override.location ?? location ?? ""),
        showLocation: !!(override.showLocation ?? showLocation),

        avatarUrl: String(
          override.avatarUrl ??
            avatarPreview ??
            previousAvatarUrl ??
            DEFAULT_COMMUNITY_AVATAR
        ),
        previousAvatarUrl: String(
          override.previousAvatarUrl ??
            previousAvatarUrl ??
            DEFAULT_COMMUNITY_AVATAR
        ),
        hasNewAvatarFile: !!(override.hasNewAvatarFile ?? !!avatar),
      };

      return snap;
    },
    [name, description, location, showLocation, avatarPreview, previousAvatarUrl, avatar]
  );

  const buildSnapshotFromFetched = useCallback((data) => {
    const serverAvatar = String(data?.avatar || DEFAULT_COMMUNITY_AVATAR);

    return {
      name: String(data?.name || ""),
      description: String(data?.description || ""),
      location: String(data?.location || ""),
      showLocation: !!data?.showLocation,

      avatarUrl: serverAvatar || DEFAULT_COMMUNITY_AVATAR,
      previousAvatarUrl: serverAvatar || DEFAULT_COMMUNITY_AVATAR,
      hasNewAvatarFile: false,
    };
  }, []);

  const currentSnapshot = useMemo(() => buildSnapshot(), [buildSnapshot]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    const a = stableStringify(initialSnapshot);
    const b = stableStringify(currentSnapshot);
    return a !== b;
  }, [initialSnapshot, currentSnapshot, stableStringify]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const onDocClickCapture = (e) => {
      if (!isDirty) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target?.closest?.("a[href]");
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href) return;

      const target = a.getAttribute("target");
      if (target && target !== "_self") return;
      if (a.hasAttribute("download")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

      if (href.startsWith("http://") || href.startsWith("https://")) {
        try {
          const u = new URL(href, window.location.origin);
          if (u.origin !== window.location.origin) return;
        } catch (err) {
          return;
        }
      }

      const resolved = new URL(href, window.location.origin);
      const to = resolved.pathname + resolved.search + resolved.hash;
      const from =
        window.location.pathname + window.location.search + window.location.hash;

      if (to === from) return;

      e.preventDefault();
      e.stopPropagation();

      pendingHrefRef.current = to;
      pendingLeaveRef.current = null;
      pendingHistoryBackRef.current = false;

      setShowUnsavedAlert(true);
    };

    document.addEventListener("click", onDocClickCapture, true);
    return () => document.removeEventListener("click", onDocClickCapture, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      const currentUrl =
        window.location.pathname + window.location.search + window.location.hash;

      if (allowPopRef.current) {
        allowPopRef.current = false;
        lastUrlRef.current = currentUrl;
        return;
      }

      if (!isDirty) {
        lastUrlRef.current = currentUrl;
        return;
      }

      try {
        navHistory.pushState(null, "", lastUrlRef.current);
      } catch (e) {}

      pendingHistoryBackRef.current = true;
      pendingLeaveRef.current = null;
      pendingHrefRef.current = null;

      setShowUnsavedAlert(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navHistory]);

  const attemptLeave = useCallback(
    (leaveFn) => {
      if (!isDirty) {
        leaveFn();
        return;
      }
      pendingLeaveRef.current = leaveFn;
      pendingHrefRef.current = null;
      pendingHistoryBackRef.current = false;
      setShowUnsavedAlert(true);
    },
    [isDirty]
  );

  const clearLeaveContext = () => {
    pendingLeaveRef.current = null;
    pendingHrefRef.current = null;
    pendingHistoryBackRef.current = false;
  };

  const closeUnsavedAlert = () => {
    setShowUnsavedAlert(false);
    clearLeaveContext();
  };

  const proceedWithoutSaving = () => {
    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
  };

  useEffect(() => {
    const loadCommunity = async () => {
      try {
        setError("");

        const url = `${API_BASE}/api/communities/${id}`;
        const response = await fetch(url);

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          console.error(
            `Помилка отримання спільноти (${response.status}):`,
            text || "Без тексту відповіді"
          );
          throw new Error(
            text || "Помилка отримання спільноти (код " + response.status + ")"
          );
        }

        const data = await response.json();

        setName(data.name || "");
        setDescription(data.description || "");
        setLocation(data.location || "");
        setShowLocation(data.showLocation || false);

        const serverAvatar = data.avatar || DEFAULT_COMMUNITY_AVATAR;
        setAvatarPreview(serverAvatar);
        setPreviousAvatarUrl(serverAvatar);

        const snap = buildSnapshotFromFetched(data);
        setInitialSnapshot(snap);

        lastUrlRef.current =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        setAvatar(null);
      } catch (err) {
        console.error("Помилка при запиті спільноти:", err);
        setError("Не вдалося завантажити спільноту");
      }
    };

    if (id) {
      loadCommunity();
    }
  }, [id, buildSnapshotFromFetched]);

  const openAvatarMenu = () => {
    setIsAvatarMenuClosing(false);
    setIsAvatarMenuOpen(true);
  };

  const closeAvatarMenu = () => {
    if (!isAvatarMenuOpen || isAvatarMenuClosing) return;
    setIsAvatarMenuClosing(true);
    setTimeout(() => {
      setIsAvatarMenuOpen(false);
      setIsAvatarMenuClosing(false);
    }, 170);
  };

  const handleAvatarClick = () => {
    if (isAvatarMenuOpen) {
      closeAvatarMenu();
    } else {
      openAvatarMenu();
    }
  };

  useEffect(() => {
    if (!isAvatarMenuOpen) return;

    const handleClickOutside = (e) => {
      if (!avatarMenuRef.current) return;
      const menuEl = avatarMenuRef.current;
      const clickInsideMenu = menuEl.contains(e.target);
      const clickOnAvatar = !!e.target.closest(".edit-community__avatar");

      if (!clickInsideMenu && !clickOnAvatar) {
        closeAvatarMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isAvatarMenuOpen]);

  const openAvatarCrop = (file) => {
    try {
      setError("");
      setSuccessMessage("");
      if (!file) return;

      if (avatarCropSrcRef.current) {
        try {
          URL.revokeObjectURL(avatarCropSrcRef.current);
        } catch {}
        avatarCropSrcRef.current = null;
      }

      const src = URL.createObjectURL(file);
      avatarCropSrcRef.current = src;

      setAvatarCropFile(file);
      setAvatarCropSrc(src);

      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

      setIsAvatarCropOpen(true);
    } catch (e) {
      console.error(e);
      setError("Не вдалося відкрити редактор аватарки спільноти");
    }
  };

  const closeAvatarCrop = () => {
    setIsAvatarCropOpen(false);
    setIsCropping(false);

    setAvatarCropFile(null);
    setAvatarCropSrc(null);
    setCroppedAreaPixels(null);

    if (avatarCropSrcRef.current) {
      try {
        URL.revokeObjectURL(avatarCropSrcRef.current);
      } catch {}
      avatarCropSrcRef.current = null;
    }
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const applyAvatarCrop = async () => {
    if (!avatarCropSrc || !croppedAreaPixels) {
      setError("Не вдалося обрізати фото. Спробуйте ще раз.");
      return;
    }

    setIsCropping(true);
    setError("");

    try {
      const blob = await getCroppedBlob(avatarCropSrc, croppedAreaPixels, 512);
      if (!blob) throw new Error("Crop blob error");

      const croppedFile = new File(
        [blob],
        `community_avatar_${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );

      setAvatar(croppedFile);

      const newPreviewUrl = URL.createObjectURL(croppedFile);
      setAvatarPreview(newPreviewUrl);

      closeAvatarCrop();
      closeAvatarMenu();
    } catch (e) {
      console.error(e);
      setError("Помилка при обрізці аватарки");
      setIsCropping(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      openAvatarCrop(file);
      closeAvatarMenu();
      try {
        e.target.value = "";
      } catch {}
    }
  };

  const saveCommunity = async () => {
    if (!name.trim()) {
      setError("Назва спільноти обов’язкова");
      return false;
    }

    if (!userId) {
      setError("Не вдалося визначити користувача");
      return false;
    }

    setIsUploading(true);
    setError("");
    setSuccessMessage("");

    let avatarUrl = avatarPreview || previousAvatarUrl || DEFAULT_COMMUNITY_AVATAR;

    if (avatar) {
      try {
        const formData = new FormData();
        formData.append("file", avatar);

        const headers = await getAuthHeaders();

        const uploadUrl = `${API_BASE}/api/communities/upload-avatar`;
        const response = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
          headers,
        });

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          console.error(
            `Помилка при завантаженні аватарки (${response.status}):`,
            text || "Без тексту відповіді"
          );
          throw new Error(
            text ||
              "Помилка при завантаженні аватарки (код " + response.status + ")"
          );
        }

        const data = await response.json();
        avatarUrl = data.url;
      } catch (err) {
        console.error(err);
        setError("Помилка при завантаженні нової аватарки");
        setIsUploading(false);
        return false;
      }
    }

    try {
      const headers = await getAuthHeaders({
        "Content-Type": "application/json",
      });

      const patchUrl = `${API_BASE}/api/communities/${id}`;
      const response = await fetch(patchUrl, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name,
          description,
          avatar: avatarUrl,
          location,
          showLocation,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        console.error(
          `Помилка при збереженні спільноти (${response.status}):`,
          text || "Без тексту відповіді"
        );
        let message = "Помилка при збереженні спільноти";
        try {
          const maybeJson = JSON.parse(text);
          if (maybeJson && maybeJson.error) {
            message = maybeJson.error;
          }
        } catch (_) {}
        throw new Error(message);
      }

      const data = await response.json().catch(() => null);
      console.log("Спільноту оновлено:", data);

      setAvatar(null);
      setPreviousAvatarUrl(avatarUrl);
      setAvatarPreview(avatarUrl);

      const newSnap = buildSnapshot({
        name,
        description,
        location,
        showLocation,
        avatarUrl,
        previousAvatarUrl: avatarUrl,
        hasNewAvatarFile: false,
      });
      setInitialSnapshot(newSnap);

      setSuccessMessage("Спільноту оновлено успішно");
      setIsUploading(false);
      return true;
    } catch (err) {
      console.error(err);
      setError(err.message || "Помилка при збереженні спільноти");
      setIsUploading(false);
      return false;
    }
  };

  const saveThenLeave = async () => {
    const ok = await saveCommunity();
    if (!ok) return;

    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
    navigate(`/communities/${id}`);
  };

  const handleSaveChanges = async () => {
    const ok = await saveCommunity();
    if (!ok) return;

    setTimeout(() => {
      navigate(`/communities/${id}`);
    }, 650);
  };

  return (
    <div className="edit-community">
      <DynamicHeader
        title="Редагувати спільноту"
        variant="solid"
        size="regular"
        hideOnScroll={true}
        showBack={true}
        onBack={() => attemptLeave(() => navigate(-1))}
        withSpacer={true}
      />

      <Alerts
        open={showUnsavedAlert}
        variant="unsavedChanges"
        onClose={closeUnsavedAlert}
        onCancel={closeUnsavedAlert}
        onSecondary={proceedWithoutSaving}
        onPrimary={saveThenLeave}
      />

      <div className="edit-community__content">
        <div className="edit-community__avatar-wrapper">
          <div className="edit-community__avatar" onClick={handleAvatarClick}>
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Community Avatar"
                className="edit-community__avatar-img"
              />
            ) : (
              <div className="edit-community__avatar-placeholder">Додати аватар</div>
            )}
          </div>

          <input
            type="file"
            id="avatar-upload"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: "none" }}
          />

          {isAvatarMenuOpen && (
            <div
              ref={avatarMenuRef}
              className={
                "edit-community__avatar-menu" +
                (isAvatarMenuClosing ? " is-closing" : " is-open")
              }
            >
              <label
                htmlFor="avatar-upload"
                className="edit-community__avatar-menu-item"
              >
                Завантажити нову
              </label>
            </div>
          )}
        </div>

        {isAvatarCropOpen && (
          <div className="ec-cropper">
            <div
              className="ec-cropper__backdrop"
              onClick={() => {
                if (!isCropping) closeAvatarCrop();
              }}
            />
            <div
              className="ec-cropper__card"
              role="dialog"
              aria-modal="true"
              aria-label="Обрізка аватарки спільноти"
            >
              <div className="ec-cropper__top">
                <button
                  type="button"
                  className="ec-cropper__btn ec-cropper__btn--ghost"
                  onClick={() => closeAvatarCrop()}
                  disabled={isCropping}
                >
                  Скасувати
                </button>
                <div className="ec-cropper__title">Аватарка</div>
                <button
                  type="button"
                  className="ec-cropper__btn ec-cropper__btn--primary"
                  onClick={applyAvatarCrop}
                  disabled={isCropping}
                >
                  {isCropping ? "Готово..." : "Готово"}
                </button>
              </div>

              <div className="ec-cropper__area">
                {avatarCropSrc && (
                  <Cropper
                    image={avatarCropSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    zoomWithScroll={true}
                    restrictPosition={true}
                    objectFit="cover"
                  />
                )}
              </div>

              <div className="ec-cropper__controls">
                <div className="ec-cropper__zoom-row">
                  <span className="ec-cropper__zoom-label">Зум</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="ec-cropper__zoom"
                    disabled={isCropping}
                  />
                </div>
                <div className="ec-cropper__hint">
                  Перетягни фото, щоб вибрати область. Масштабуй повзунком або колесом миші.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="edit-community__info">
          <label htmlFor="name" className="edit-community__label">
            Назва спільноти
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="edit-community__input"
            placeholder="Введіть назву спільноти"
          />
        </div>

        <div className="edit-community__info">
          <label htmlFor="description" className="edit-community__label">
            Опис спільноти
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={160}
            className="edit-community__textarea"
            placeholder="Короткий опис (до 160 символів)"
          />
          <div className="edit-community__char-counter">
            Залишилось {160 - description.length} символів
          </div>
        </div>

        <div className="edit-community__info">
          <label htmlFor="location" className="edit-community__label">
            Місто
          </label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="edit-community__input"
            placeholder="Наприклад, Київ"
          />
          <div className="edit-community__checkbox">
            <input
              type="checkbox"
              id="showLocation"
              checked={showLocation}
              onChange={(e) => setShowLocation(e.target.checked)}
            />
            <label htmlFor="showLocation">
              Показувати місто в профілі спільноти
            </label>
          </div>
        </div>

        <div className="edit-community__actions">
          <button
            className={"edit-community__save-btn" + (!isDirty ? " is-disabled" : "")}
            onClick={handleSaveChanges}
            disabled={isUploading || !isDirty}
            aria-disabled={isUploading || !isDirty}
            title={!isDirty ? "Немає змін для збереження" : ""}
          >
            {isUploading ? "Завантаження..." : "Зберегти зміни"}
          </button>
        </div>

        {error && <p className="edit-community__error">{error}</p>}
        {successMessage && (
          <p className="edit-community__success">{successMessage}</p>
        )}
      </div>
    </div>
  );
};

export default EditCommunity;